import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { AlertService } from '../../../services/common/alert.service';
import { AuthService } from '../../../services/common/auth.service';
import { MatterStoreService } from '../../../services/matters/matter-store.service';
import { MatterHistoryService } from '../../../services/matters/matter-history.service';

declare var window: any;

@Component({
  selector: 'app-matter-histories',
  templateUrl: './matter-histories.component.html',
  styleUrls: ['./matter-histories.component.css'],
})
export class MatterHistoriesComponent implements OnInit, OnDestroy {
  @Input() expandingAllLevels: number = 0;
  @Input() expandingChildren: number = 0;

  @Input() loadReplies: number;

  @Input() matter_id: number;

  @Input() parent_id: number;

  @Output() historyCount: EventEmitter<number> = new EventEmitter();

  count: number = 0;

  currentUserId: number;

  histories_loading: boolean = true;

  matterHistories: Array<any> = [];

  search_params: any = {};

  search_options: any = {
    amounts: [],
    matter_history_types: [],
    authors: [],
  };

  searchFormSubmitting: boolean = false;

  initDone: boolean = false;

  initialLoading: boolean = true;

  selectedHistoryId: number = 0;

  selectedRootHistoryId: number = 0;

  selectedHistoryHandled: boolean = false;

  constructor(private activatedRoute: ActivatedRoute, private alertService: AlertService, public authService: AuthService, private matterStoreService: MatterStoreService, private matterHistoryService: MatterHistoryService) {
    this.matterStoreService.matterHistoriesComponent = this;
  }

  // Component lifecycle events
  ngOnInit() {
    this.selectedHistoryId = this.routeSelectedHistoryId();
    if (this.parent_id) {
      this.search_params.matter_id = this.matter_id;
      this.search_params.parent_id = this.parent_id;
      this.loadHistories();
    }
    this.initDone = true;
  }

  ngOnDestroy() {
    this.matterStoreService.matterHistoriesComponent = null;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.parent_id && changes.matter_id && changes.matter_id.currentValue) {
      this.selectedHistoryId = this.routeSelectedHistoryId();
      this.prepareComponent();
    }
    if (changes.expandingAllLevels) {
      if (changes.expandingAllLevels.currentValue > 10000) this.expandHistories(true);
      else this.collapseHistories();
    } else if (changes.expandingChildren) {
      if (changes.expandingChildren.currentValue > 10000) this.expandHistories();
      else this.collapseHistories();
    }
    if (!this.initDone) return;
    if (changes.loadReplies && changes.loadReplies.currentValue && !isNaN(Number(changes.loadReplies.currentValue)) && Number(changes.loadReplies.currentValue) > 0) this.loadHistories();
  }

  // Component methods
  collapseChildren(matterHistory: any) {
    matterHistory.expandingAllLevels = Math.random() * 10000;
    matterHistory.expandingChildren = Math.random() * 10000;
  }

  collapseHistories() {
    this.expandingAllLevels = Math.random() * 10000;
    this.expandingChildren = Math.random() * 10000;
    for (let matterHistory of this.matterHistories) this.collapseHistory(matterHistory);
  }

  collapseHistory(matterHistory: any) {
    matterHistory.expanded = false;
    matterHistory.expandingAllLevels = Math.random() * 10000;
    matterHistory.expandingChildren = Math.random() * 10000;
  }

  editHistory(matterHistory: any) {
    matterHistory.editing = true;
  }

  expandAllLevels(matterHistory: any) {
    matterHistory.expandingAllLevels = 100000 + Math.random() * 10000;
  }

  expandChildren(matterHistory: any) {
    matterHistory.expandingChildren = 100000 + Math.random() * 10000;
  }

  expandHistories(allLevels: boolean = false) {
    for (let matterHistory of this.matterHistories) this.expandHistory(matterHistory, allLevels);
  }

  expandHistory(matterHistory: any, allLevels: boolean = false) {
    if (matterHistory.expanded) return Promise.resolve(matterHistory.historyInfo);
    matterHistory.expanded = true;
    matterHistory.historyError = null;
    return this.matterHistoryService.getHistoryInfo(matterHistory.id).then((response_json) => {
      matterHistory.historyInfo = response_json;
      if (allLevels) matterHistory.expandingAllLevels = 100000 + Math.random() * 10000;
      matterHistory.historyInfo.messageStyle = matterHistory.historyInfo.color_value ? 'color: ' + (matterHistory.historyInfo.color_value.indexOf('#') > -1 ? matterHistory.historyInfo.color_value : '#' + matterHistory.historyInfo.color_value) : '';
      matterHistory.historyInfo.wrote = `${matterHistory.historyInfo.updated_at_time ? matterHistory.historyInfo.updated_at_time : matterHistory.historyInfo.created_at_time} ${
        matterHistory.historyInfo.created_by_user ? (matterHistory.historyInfo.updated_by_user ? matterHistory.historyInfo.updated_by_user : matterHistory.historyInfo.created_by_user) + ' wrote: ' : ''
      }`;
      matterHistory.historyInfo.title = this.titleize(matterHistory.historyInfo.title);
      for (let bodyLink of matterHistory.historyInfo.body_links) {
        bodyLink.titleized = this.titleize(bodyLink.link_type);
      }
      return response_json;
    }).catch((error) => {
      matterHistory.historyError = error && error.status === 404 ? 'This history is no longer available. Refresh the history list to remove it.' : 'This history could not be loaded. Refresh the history list and try again.';
    });
  }

  historyFormResult(matterHistory: any, result: string) {
    if (result == 'Form Cancelled') matterHistory.editing = false;
    else if (result == 'saved') {
      this.alertService.alert_status_success('History saved');
      matterHistory.editing = false;
      this.matterHistoryService.getHistoryInfo(matterHistory.id).then((response_json) => {
        matterHistory.historyInfo = response_json;
      });
    }
  }

  loadHistories(expandAll: boolean = false) {
    this.searchFormSubmitting = true;
    this.matterHistoryService
      .getMatterHistories(this.search_params)
      .then((response_json) => {
        this.searchFormSubmitting = false;
        this.count = response_json.count || 0;
        this.matterHistories = response_json.matter_histories;
        this.selectedRootHistoryId = Number(response_json.selected_root_history_id || this.selectedRootHistoryId || 0);
        this.histories_loading = false;
        if (!this.currentUserId) {
          this.authService.fetchUserId().then((response_json) => {
            this.currentUserId = response_json.current_user_id;
          });
        }
        for (let matterHistory of this.matterHistories) {
          matterHistory.replyCountStr = this.replyCount(matterHistory.reply_count);
          matterHistory.loadReplies = matterHistory.reply_count;
          matterHistory.second_descendants_count = matterHistory.descendants_count - matterHistory.reply_count;
        }
        //this.historyCount.emit(this.matterHistories.length);
        for (let matterHistory of this.matterHistories) {
          matterHistory.titleHTML = this.removeHTML(matterHistory.title);
          for (let headerLink of matterHistory.header_links) headerLink.titleized = this.titleize(headerLink.link_type);
        }
        if (this.initialLoading) {
          if (this.expandingAllLevels > 10000) this.expandHistories(true);
          else if (this.expandingChildren > 10000) this.expandHistories();
        } else if (expandAll) this.expandHistories(true);
        this.openSelectedHistory();
        this.initialLoading = false;
      })
      .catch(() => {
        this.searchFormSubmitting = false;
        this.initialLoading = false;
        this.alertService.alert_status_warning('Error loading matter histories');
      });
  }

  /**
   * Page changed
   * @param {any} $event Page change event with new page number
   */
  pageChanged($event: any) {
    this.search_params.page = $event.page;
    this.loadHistories();
  }

  prepareComponent() {
    this.matterHistoryService.getSearchOptions(this.matter_id).then((response_json) => {
      this.search_options = response_json;
      this.histories_loading = false;
      this.search_params = response_json.user_settings;
      this.search_params.matter_id = this.matter_id;
      this.search_params.parent_id = this.parent_id;
      if (this.selectedHistoryId) {
        this.search_params.selected_history_id = this.selectedHistoryId;
        this.search_params.matter_history_type_id = 'all';
        this.search_params.created_by = 'all';
        this.search_params.message = null;
        this.search_params.tags = null;
      }
      this.search_params.matter_history_type_id;
      let matter_history_type_found = false;
      for (let matter_history_type of this.search_options.matter_history_types)
        if (this.search_params.matter_history_type_id == matter_history_type.value) {
          matter_history_type_found = true;
          break;
        }
      if (!matter_history_type_found) this.search_params.matter_history_type_id = 'all';
      this.loadHistories();
    });
  }

  removeHTML(str: string): string {
    return window.removeHTMLTags(str);
  }

  replyCount(count: number): string {
    return count == 1 ? '1 reply' : `${count} replies`;
  }

  replyFormResult(matterHistory: any, result: string) {
    if (result == 'Form Cancelled') matterHistory.replying = false;
    else if (result == 'saved') {
      this.alertService.alert_status_success('History reply added');
      matterHistory.replying = false;
      matterHistory.loadReplies = (matterHistory.loadReplies || 0) + 1;
      matterHistory.reply_count = (matterHistory.reply_count || 0) + 1;
      matterHistory.replyCountStr = this.replyCount(matterHistory.reply_count);
    }
  }

  replyToHistory(matterHistory: any) {
    this.expandHistory(matterHistory);
    matterHistory.replying = true;
  }

  openSelectedHistory() {
    if (!this.selectedHistoryId || this.selectedHistoryHandled) return;

    let historyIdToOpen = this.parent_id ? this.selectedHistoryId : this.selectedRootHistoryId || this.selectedHistoryId;
    let selectedHistory = this.matterHistories.find((matterHistory: any) => Number(matterHistory.id) === Number(historyIdToOpen));
    if (!selectedHistory) return;

    let exactHistorySelected = Number(selectedHistory.id) === Number(this.selectedHistoryId);
    selectedHistory.selectedFromLink = exactHistorySelected;
    this.selectedHistoryHandled = exactHistorySelected;
    this.expandHistory(selectedHistory, true).then(() => {
      if (exactHistorySelected) this.scrollToSelectedHistory();
    });
  }

  routeSelectedHistoryId(): number {
    let queryParams: any = this.activatedRoute.snapshot.queryParams || {};
    return Number(queryParams.selected_history_id || queryParams.history_id || 0);
  }

  scrollToSelectedHistory() {
    window.setTimeout(() => {
      let element = document.getElementById(`matter-history-${this.selectedHistoryId}`);
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
  }

  titleize(text: string): string {
    if (!text) text = '';
    let result: Array<string> = [];
    for (let word of text.split(' ')) {
      if (word && word.length > 0) {
        result.push(word[0].toUpperCase() + word.slice(1));
      }
    }
    return result.join(' ');
  }

  toggleHistory(matterHistory: any) {
    matterHistory.expanded = !matterHistory.expanded;
    if (matterHistory.expanded)
      this.matterHistoryService.getHistoryInfo(matterHistory.id).then((response_json) => {
        matterHistory.historyInfo = response_json;
      });
  }
}
